/**
 * AssetGenerator — AI image / video generation mockup.
 * No real provider wired; UI demonstrates the workflow only.
 */
import { useState } from "react";
import { Sparkles, Image as ImageIcon, Loader2, Wand2, Download, Plus } from "lucide-react";
import MarketingShell from "@/components/MarketingShell";
import { toast } from "sonner";

const RECENT = [
  { id: 1, prompt: "Cardiologist reviewing tablet in modern exam room, navy + cool tones, natural light", tag: "subject:provider", color: "#5b8def" },
  { id: 2, prompt: "Abstract navy gradient background with subtle data viz elements", tag: "background:abstract", color: "#1b365d" },
  { id: 3, prompt: "Patient-facing dashboard on iPad, hand interacting, clinical setting", tag: "product:rpm", color: "#6fb8ff" },
  { id: 4, prompt: "Primary care physician with stethoscope, editorial photography, no logos", tag: "subject:provider", color: "#2e63b8" },
  { id: 5, prompt: "Multi-device hero — laptop + tablet + phone showing Echo EHR UI", tag: "product:ehr", color: "#5b8def" },
  { id: 6, prompt: "RCM software UI on macOS browser, clean light theme", tag: "product:rcm", color: "#e4eaf5" },
];

export default function AssetGenerator() {
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("nano-banana");
  const [ratio, setRatio] = useState("1:1");
  const [generating, setGenerating] = useState(false);

  const generate = () => {
    if (!prompt.trim()) return toast.error("Enter a prompt");
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      toast.success("Generated · added to Brand Library");
    }, 1800);
  };

  return (
    <MarketingShell
      title="Asset Generator"
      subtitle="AI-generated imagery for templates · MOCKED"
      actions={<button className="btn"><Plus style={{ width: 13, height: 13 }} /> New asset</button>}
    >
      <div data-testid="asset-generator">
        <div className="mkt-card mkt-card-pad" style={{ marginBottom: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Prompt</div>
          <textarea
            className="input"
            rows={3}
            placeholder="Describe the image — Enable's brand prompt prefix is applied automatically (navy + cool tones, editorial healthcare photography, no people if subject is product, etc.)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="asset-prompt"
            style={{ resize: "vertical" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>Provider</div>
              <div className="mkt-segmented">
                {[
                  { k: "nano-banana", l: "Nano Banana" },
                  { k: "gpt-image-1", l: "GPT Image 1" },
                  { k: "imagen-4", l: "Imagen 4" },
                ].map((p) => (
                  <button key={p.k} onClick={() => setProvider(p.k)} className={provider === p.k ? "active" : ""}>{p.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>Aspect</div>
              <div className="mkt-segmented">
                {["1:1", "4:5", "9:16", "16:9", "3:1"].map((r) => (
                  <button key={r} onClick={() => setRatio(r)} className={ratio === r ? "active" : ""}>{r}</button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={generate} className="btn btn-primary" disabled={generating} data-testid="asset-generate">
              {generating ? <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Generating…</> : <><Wand2 style={{ width: 13, height: 13 }} /> Generate</>}
            </button>
          </div>
        </div>

        <h3 className="card-title" style={{ marginBottom: 12 }}>Recent generations</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {RECENT.map((r) => (
            <div key={r.id} className="mkt-card is-interactive" data-testid={`asset-card-${r.id}`}>
              <div style={{ height: 180, background: r.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ImageIcon style={{ width: 36, height: 36, color: "rgba(255,255,255,0.65)" }} />
              </div>
              <div style={{ padding: "10px 14px" }}>
                <div style={{ fontSize: 11.5, color: "var(--text-secondary)", lineHeight: 1.45, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {r.prompt}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <span className="tag tag-gray">{r.tag}</span>
                  <button className="btn" style={{ height: 24, padding: "0 8px", fontSize: 11 }}>
                    <Download style={{ width: 11, height: 11 }} /> Save
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </MarketingShell>
  );
}
